// Brand detection for subscription services
// Uses keyword matching + Google Favicon API for logos

export interface Brand {
  name: string;
  domain: string;
  color: string;
  keywords: string[];
}

// Popular subscription brands with their domains and brand colors
const KNOWN_BRANDS: Brand[] = [
  // Streaming
  { name: 'Netflix', domain: 'netflix.com', color: '#E50914', keywords: ['netflix'] },
  { name: 'Spotify', domain: 'spotify.com', color: '#1DB954', keywords: ['spotify'] },
  { name: 'Disney+', domain: 'disneyplus.com', color: '#113CCF', keywords: ['disney', 'disney+'] },
  { name: 'Apple TV+', domain: 'apple.com', color: '#000000', keywords: ['apple tv', 'appletv'] },
  {
    name: 'Amazon Prime',
    domain: 'amazon.com',
    color: '#00A8E1',
    keywords: ['amazon', 'prime video', 'prime'],
  },
  {
    name: 'YouTube',
    domain: 'youtube.com',
    color: '#FF0000',
    keywords: ['youtube', 'youtube premium', 'youtube music'],
  },
  { name: 'HBO Max', domain: 'hbomax.com', color: '#B537F2', keywords: ['hbo', 'hbo max'] },
  { name: 'Apple Music', domain: 'music.apple.com', color: '#FA243C', keywords: ['apple music'] },
  { name: 'Deezer', domain: 'deezer.com', color: '#A238FF', keywords: ['deezer'] },
  {
    name: 'Canal+',
    domain: 'canalplus.com',
    color: '#1A1A1A',
    keywords: ['canal', 'canal+', 'canal plus', 'mycanal'],
  },
  { name: 'Crunchyroll', domain: 'crunchyroll.com', color: '#F47521', keywords: ['crunchyroll'] },
  { name: 'Paramount+', domain: 'paramountplus.com', color: '#0064FF', keywords: ['paramount'] },

  // Software & Cloud
  {
    name: 'Adobe',
    domain: 'adobe.com',
    color: '#FF0000',
    keywords: ['adobe', 'creative cloud', 'photoshop', 'lightroom', 'illustrator', 'premiere'],
  },
  {
    name: 'Microsoft 365',
    domain: 'microsoft.com',
    color: '#0078D4',
    keywords: ['microsoft', 'office 365', 'microsoft 365', 'onedrive'],
  },
  {
    name: 'Google One',
    domain: 'google.com',
    color: '#4285F4',
    keywords: ['google one', 'google storage', 'google drive'],
  },
  { name: 'iCloud', domain: 'icloud.com', color: '#3693F3', keywords: ['icloud', 'icloud+'] },
  { name: 'Dropbox', domain: 'dropbox.com', color: '#0061FF', keywords: ['dropbox'] },
  { name: 'Notion', domain: 'notion.so', color: '#000000', keywords: ['notion'] },
  {
    name: 'ChatGPT',
    domain: 'openai.com',
    color: '#10A37F',
    keywords: ['chatgpt', 'openai', 'gpt'],
  },
  {
    name: 'GitHub',
    domain: 'github.com',
    color: '#181717',
    keywords: ['github', 'github copilot'],
  },
  { name: 'Figma', domain: 'figma.com', color: '#F24E1E', keywords: ['figma'] },
  { name: 'Slack', domain: 'slack.com', color: '#4A154B', keywords: ['slack'] },
  { name: 'Zoom', domain: 'zoom.us', color: '#2D8CFF', keywords: ['zoom'] },

  // Gaming
  {
    name: 'PlayStation',
    domain: 'playstation.com',
    color: '#003791',
    keywords: ['playstation', 'ps plus', 'psn', 'ps+'],
  },
  {
    name: 'Xbox',
    domain: 'xbox.com',
    color: '#107C10',
    keywords: ['xbox', 'game pass', 'xbox live'],
  },
  {
    name: 'Nintendo',
    domain: 'nintendo.com',
    color: '#E60012',
    keywords: ['nintendo', 'switch online'],
  },
  { name: 'Steam', domain: 'steampowered.com', color: '#1B2838', keywords: ['steam'] },

  // Fitness & Health
  {
    name: 'Basic-Fit',
    domain: 'basic-fit.com',
    color: '#FE5000',
    keywords: ['basic-fit', 'basicfit', 'basic fit'],
  },
  { name: 'Fitness Park', domain: 'fitnesspark.fr', color: '#FF6B00', keywords: ['fitness park'] },
  { name: 'Headspace', domain: 'headspace.com', color: '#F47D31', keywords: ['headspace'] },

  // Food & Delivery
  {
    name: 'Uber Eats',
    domain: 'ubereats.com',
    color: '#06C167',
    keywords: ['uber eats', 'ubereats'],
  },
  { name: 'Uber', domain: 'uber.com', color: '#000000', keywords: ['uber'] },
  { name: 'Deliveroo', domain: 'deliveroo.com', color: '#00CCBC', keywords: ['deliveroo'] },
  { name: 'Just Eat', domain: 'justeat.com', color: '#FF8000', keywords: ['just eat'] },
  { name: 'HelloFresh', domain: 'hellofresh.com', color: '#94C83D', keywords: ['hellofresh'] },

  // Telecom & Internet
  { name: 'Orange', domain: 'orange.fr', color: '#FF6600', keywords: ['orange'] },
  { name: 'SFR', domain: 'sfr.fr', color: '#E4002B', keywords: ['sfr'] },
  { name: 'Free', domain: 'free.fr', color: '#CD1E25', keywords: ['free mobile', 'freebox'] },
  { name: 'Bouygues', domain: 'bouyguestelecom.fr', color: '#0055A4', keywords: ['bouygues'] },
  { name: 'Sosh', domain: 'sosh.fr', color: '#FF6600', keywords: ['sosh'] },

  // Insurance & Finance
  { name: 'MACIF', domain: 'macif.fr', color: '#004F9E', keywords: ['macif'] },
  { name: 'MAIF', domain: 'maif.fr', color: '#00965E', keywords: ['maif'] },
  { name: 'AXA', domain: 'axa.fr', color: '#00008F', keywords: ['axa'] },
  { name: 'Allianz', domain: 'allianz.fr', color: '#003781', keywords: ['allianz'] },
  { name: 'N26', domain: 'n26.com', color: '#36A18B', keywords: ['n26'] },
  { name: 'Revolut', domain: 'revolut.com', color: '#0075EB', keywords: ['revolut'] },

  // Shopping & Subscriptions
  { name: 'Amazon', domain: 'amazon.com', color: '#FF9900', keywords: ['amazon prime'] },
  { name: 'Fnac', domain: 'fnac.com', color: '#E4A400', keywords: ['fnac', 'fnac+', 'fnac plus'] },
  { name: 'Costco', domain: 'costco.com', color: '#E31837', keywords: ['costco'] },

  // Transport
  {
    name: 'SNCF',
    domain: 'sncf.com',
    color: '#82188B',
    keywords: ['sncf', 'tgv max', 'carte avantage'],
  },
  {
    name: 'Navigo',
    domain: 'iledefrance-mobilites.fr',
    color: '#6F2282',
    keywords: ['navigo', 'ratp', 'iledefrance'],
  },
  { name: 'Velib', domain: 'velib-metropole.fr', color: '#8CC63F', keywords: ['velib'] },
  { name: 'Lime', domain: 'li.me', color: '#00DE00', keywords: ['lime'] },

  // News & Media
  { name: 'Le Monde', domain: 'lemonde.fr', color: '#1A1A1A', keywords: ['le monde'] },
  { name: 'Le Figaro', domain: 'lefigaro.fr', color: '#0056A6', keywords: ['le figaro', 'figaro'] },
  { name: 'Les Echos', domain: 'lesechos.fr', color: '#003D7A', keywords: ['les echos', 'echos'] },
  { name: 'The Economist', domain: 'economist.com', color: '#E3120B', keywords: ['economist'] },
  { name: 'Medium', domain: 'medium.com', color: '#000000', keywords: ['medium'] },

  // Education
  {
    name: 'LinkedIn',
    domain: 'linkedin.com',
    color: '#0A66C2',
    keywords: ['linkedin', 'linkedin premium'],
  },
  { name: 'Duolingo', domain: 'duolingo.com', color: '#58CC02', keywords: ['duolingo'] },
  { name: 'Coursera', domain: 'coursera.org', color: '#0056D2', keywords: ['coursera'] },

  // VPN & Security
  { name: 'NordVPN', domain: 'nordvpn.com', color: '#4687FF', keywords: ['nordvpn', 'nord vpn'] },
  {
    name: 'ExpressVPN',
    domain: 'expressvpn.com',
    color: '#DA3940',
    keywords: ['expressvpn', 'express vpn'],
  },
  { name: 'Bitwarden', domain: 'bitwarden.com', color: '#175DDC', keywords: ['bitwarden'] },
  { name: '1Password', domain: '1password.com', color: '#1A8CFF', keywords: ['1password'] },

  // Other
  { name: 'EDF', domain: 'edf.fr', color: '#FF6600', keywords: ['edf', 'electricite'] },
  { name: 'Engie', domain: 'engie.fr', color: '#009DE0', keywords: ['engie'] },
  { name: 'Veolia', domain: 'veolia.com', color: '#00467F', keywords: ['veolia'] },
];

/**
 * Detect a brand from a subscription description/name
 */
export function detectBrand(description: string): Brand | null {
  const lower = description.toLowerCase().trim();
  for (const brand of KNOWN_BRANDS) {
    for (const keyword of brand.keywords) {
      if (lower.includes(keyword)) {
        return brand;
      }
    }
  }
  return null;
}

/**
 * Get the logo URL for a brand using Google Favicon API
 */
export function getBrandLogoUrl(domain: string, size = 64): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

/**
 * Get logo URL from a subscription description
 * Returns the brand color and logo URL if detected, or null
 */
export function getSubscriptionLogo(
  description: string
): { logoUrl: string; color: string; name: string } | null {
  const brand = detectBrand(description);
  if (!brand) return null;
  return {
    logoUrl: getBrandLogoUrl(brand.domain, 128),
    color: brand.color,
    name: brand.name,
  };
}
